import { $fetch, FetchError } from 'ofetch'
import { defineStore } from 'pinia';
import { useFlashStore } from '~~/store/flashStore';
import { parseError } from './errors';
import { setCookie } from "h3";
import { splitCookiesString } from 'set-cookie-parser'
import type { NuxtApp } from '#app';
import { useMainStore } from '~/store/mainState';
import { serialize } from 'object-to-formdata';


export const apiFetch = $fetch.create({
    // baseURL: import.meta.server ? 'http://test.dev-triya.ru/' : '/',
    baseURL: import.meta.server ? 'https://www.triya.ru/' : '/',
    // baseURL: import.meta.server ? 'http://lumen.triya.ru/' : '/',
});

interface ICookieParam {
    name: string;
    value: string;
    params: {
        expires?: Date;
        maxAge?: number;
        path?: string;
    };
}

function parseCookieString(raw: string) {

    const parts = raw.split(';').map((el) => el.trim());

    const nameParts = parts.shift()!.split('=');

    const result: ICookieParam = {
        name: nameParts[0]!.trim(),
        value: nameParts[1]!.trim(),
        params: {}
    };

    parts.forEach((el) => {
        const row = el.split('=');

        switch (row[0]) {
            case 'expires':
                result.params.expires = new Date(row[1]!);
                return;
            case 'Max-Age':
                result.params.maxAge = Number(row[1]);
                return;
            case 'path':
                result.params.path = row[1];
                return;
        }
    });

    return result;
}

export const useApi = defineStore('api', () => {

    const activeRequests = reactive(new Set<string>());

    const flashStore = useFlashStore();

    function applyCookie(app: NuxtApp, value: string) {
        const cookies = splitCookiesString(value);
        cookies.forEach((el) => {
            const result = parseCookieString(el);
            setCookie(app.ssrContext!.event, result.name, result.value, result.params);
        });
    }

    function get<T = any>(endpoint: string, body?: {[key: string]: string | number}) {

        if (typeof body?._c === 'undefined') {
            const region = useMainStore().location?.city.content;
            if (region) {
                if (!body) {
                    body = {_c: region};
                } else {
                    body._c = region;
                }
            }
        }

        if (body) {
            endpoint += endpoint.includes('?') ? '&' : '?' + Object.entries(body).map(e => e.map(encodeURIComponent).join("=")).join("&");
        }
        return request<T>(endpoint, undefined, 'get');
    }

    function post<T = any>(endpoint: string, body?: any) {
        return request<T>(endpoint, body, 'post');
    }

    async function request<T = any>(endpoint: string, body?: any, type: 'get' | 'post' = 'post') {

        try {
            activeRequests.add(endpoint);

            const params: any = {
                body: body,
                method: type,
                responseType: 'json',
                headers: {
                    'x-device': 'test',
                }
            };

            if (import.meta.server && !endpoint.startsWith('https://')) {
                const app = useNuxtApp();
                if (app.ssrContext?.event) {
                    params['headers'] = useRequestHeaders(['cookie', 'X-Real-IP', 'user-agent']) as any;
                    params['headers']['x-request-id'] = app.ssrContext.event.context.requestId;
                    params['headers']['x-device'] = 'test';
                    if (!params.body) {
                        params.body = {};
                    }
                    const data = await apiFetch.raw<T>(endpoint, params);

                    const rawCookie = data.headers.get('set-cookie');
                    if (rawCookie) {
                        applyCookie(app, rawCookie);
                    }

                    return data._data; 
                }
            }

            return await apiFetch<T>(endpoint, params);
        } catch (error) {
            errorHandler(error);
        }finally {
            activeRequests.delete(endpoint);
        }
    }

    async function formRequest<T = any>(endpoint: string, body: any) {

        try {
            activeRequests.add(endpoint);
            body = body ?? {};

            const form = serialize(body);
            
            return await apiFetch<T>(endpoint, {
                body: form,
                method: 'post',
                responseType: 'json',
                headers: {
                    ...(useRequestHeaders(['cookie']) as any),
                }
            })

        } catch (error) {
            errorHandler(error)
        }finally {
            activeRequests.delete(endpoint);
        }
    }

    function errorHandler(error: any) {
        if (error instanceof FetchError && error.response?._data?.public_error) {
            const info = parseError(error.response._data);

            flashStore.setFlash(info.message, 'error', 5000);

            if (!info.stop) {
                throw error;
            }
        } else if (error instanceof FetchError) {
            throw createError({statusCode: error.statusCode});
        } else {
            flashStore.setFlash('Неизвестная ошибка', 'error', 5000);
            throw error;
        }
    }

    const pending = computed(() => !!activeRequests.size)

    return { request, formRequest, get, post, pending };
});

